// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { type AskPunttReviewBlocked } from "@mg/schemas/src/christo/askPunttReview";
import { CaretUp, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Badge, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentJudgmentActions } from "./AskV2ReviewCommentJudgmentActions";
import { AskV2ReviewTagFilter } from "./AskV2ReviewTagFilter";
import { CommentTagBadges } from "./CommentTagBadges";
import {
  citedRuleBrand,
  citedRuleScopeChoices,
  type RejectionScope,
} from "./rememberedSentence";
import {
  defaultActiveReviewCommentTags,
  REVIEW_COMMENT_TAG_IDS,
  type CommentWithOptionalTags,
  type ReviewCommentTagId,
} from "./reviewCommentTags";

import {
  AskPunttReviewCommentBadge,
  AskPunttReviewCommentBody,
  AskPunttReviewCommentSource,
  type AssetFile,
  formatReviewCommentTime,
  reviewCommentAuthorLabel,
  reviewCommentCollapsedSections,
  reviewCommentTitle,
} from "../../components/ask-puntt-review";
import { claimCitationsFor } from "../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../plugins/rehype-claim-citation";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  KNOWLEDGE_DATABASE_PATH,
} from "../../routes/settings/routes/knowledge-database/search";

type AskV2ReviewCommentsListProps = {
  comments: readonly CommentWithOptionalTags[];
  /**
   * Checks that did not finish, with the reason each gave. Absent on a review
   * written before warhol carried them and on one where every check ran.
   */
  blocked?: readonly AskPunttReviewBlocked[];
  commentNumbers?: Readonly<Record<string, number>>;
  contextFiles?: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
  selectedCommentId?: string | null;
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
  activeTags?: ReadonlySet<ReviewCommentTagId>;
  showTagFilter?: boolean;
  onToggleTag?: (tag: ReviewCommentTagId, checked: boolean) => void;
  includeRejectedComments?: boolean;
  rejectedCommentCount?: number;
  onToggleIncludeRejected?: () => void;
  judgmentPending?: boolean;
  onJudge?: (
    commentId: string,
    liked: boolean | null,
    scope?: RejectionScope,
  ) => void;
  onSelect: (commentId: string) => void;
  onClear: () => void;
};

function CommentSeverityBadge({
  severity,
}: {
  severity: CommentWithOptionalTags["severity"];
}) {
  const { t } = useTranslation("ui");
  if (!severity) return null;

  const label =
    severity === "high"
      ? t("highConfidence")
      : severity === "medium"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <Badge
      size="1"
      variant="soft"
      color="gray"
      className="inline-flex items-center gap-1"
      data-testid="ask-v2-review-comment-confidence-badge"
    >
      <CaretUp size={10} weight="bold" aria-hidden="true" />
      {label}
    </Badge>
  );
}

function CommentIdentityRow({
  id,
  commentNumber,
  authorLabel,
  createdAt,
}: {
  id: string;
  commentNumber: number;
  authorLabel: string;
  createdAt: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <AskPunttReviewCommentBadge
        number={commentNumber}
        data-testid={`ask-v2-review-comment-index-${id}`}
      />
      <div
        className="flex min-w-0 items-center gap-2 text-[12px] text-puntt-neutral-gray-9"
        data-testid={`ask-v2-review-comment-author-${id}`}
      >
        <span className="truncate font-medium text-puntt-neutral-gray-11">
          {authorLabel}
        </span>
        <span className="shrink-0">{formatReviewCommentTime(createdAt)}</span>
      </div>
    </div>
  );
}

function CollapsedCommentPreview({
  comment,
}: {
  comment: CommentWithOptionalTags;
}) {
  const sections = reviewCommentCollapsedSections(comment.description);
  if (sections.length === 0) {
    const preview = comment.description.trim().split("\n").slice(1).join(" ");
    if (!preview) return null;
    return (
      <Text as="p" size="3" className="mt-2 text-puntt-neutral-gray-10">
        {preview}
      </Text>
    );
  }

  return (
    <div
      className="mt-2 space-y-2"
      data-testid={`ask-v2-review-comment-preview-${comment._id}`}
    >
      {sections.map((section) => (
        <Text
          as="p"
          key={section.label}
          size="3"
          className="text-puntt-neutral-gray-11"
          data-testid={`ask-v2-review-comment-section-${comment._id}-${section.label.toLowerCase()}`}
        >
          <span className="font-semibold text-puntt-neutral-gray-12">
            {section.label}:
          </span>{" "}
          {section.body}
        </Text>
      ))}
    </div>
  );
}

export function AskV2ReviewCommentsList({
  comments,
  commentNumbers: commentNumbersProp,
  blocked = [],
  contextFiles = [],
  reviewFiles = [],
  selectedCommentId = null,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  activeTags = defaultActiveReviewCommentTags(),
  showTagFilter = false,
  onToggleTag = () => undefined,
  includeRejectedComments = false,
  rejectedCommentCount = 0,
  onToggleIncludeRejected,
  judgmentPending = false,
  onJudge,
  onSelect,
  onClear,
}: AskV2ReviewCommentsListProps) {
  const { t } = useTranslation("ui");
  const commentNumbers = useMemo(() => {
    if (commentNumbersProp) return commentNumbersProp;
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of comments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [commentNumbersProp, comments]);

  // Derived once per comment list, not inside the render map. Called inline it
  // handed `AskPunttReviewCommentBody` a fresh array identity on every render
  // of this list -- and that array is what `Markdown` keys its rehype-plugin
  // memo on, so every unrelated re-render (a tag filter, a selection) rebuilt
  // the unified processor for every comment on screen. `ChatMessage` already
  // memoises the same derivation; this is the list doing the same.
  const claimCitationsByComment = useMemo(() => {
    const byId = new Map<string, LinkableClaim[]>();
    for (const comment of comments) {
      byId.set(comment._id, claimCitationsFor(comment));
    }
    return byId;
  }, [comments]);

  const authorOptions = {
    aiLabel: t("aiReviewer"),
    currentUserId,
    currentUserName,
    authorNameByUserId,
    unknownAuthorLabel: t("member"),
  };

  // Why the list is empty, which is not always "you filtered it". A review
  // that found nothing -- or one whose checks were all blocked -- used to read
  // "No findings match the selected types" while every type was still
  // selected, and with `showTagFilter` false the control that copy blames is
  // not even on screen.
  const rejectedRowsHidden =
    rejectedCommentCount > 0 && !includeRejectedComments;
  const tagFilterNarrows = REVIEW_COMMENT_TAG_IDS.some(
    (tag) => !activeTags.has(tag),
  );
  const emptyStateTestId = rejectedRowsHidden
    ? "ask-v2-review-all-rejected-empty"
    : tagFilterNarrows
      ? "ask-v2-review-tag-filter-empty"
      : "ask-v2-review-no-findings-empty";
  const emptyStateCopy = rejectedRowsHidden
    ? t("askV2.review.allRejectedEmpty")
    : tagFilterNarrows
      ? t("askV2.review.tagFilterEmpty")
      : t("askV2.review.noFindings");

  // `artwork` rides on every blocked entry precisely so a review of several
  // pairs can say which one a check could not run on. Rendering the check name
  // alone printed "typography — no text layer" twice, identical rows a person
  // cannot act on. Naming the artwork on a single-artwork review would be
  // noise, so only disambiguate when the list actually spans more than one.
  const blockedSpansArtworks =
    new Set(blocked.map((entry) => entry.artwork)).size > 1;

  useLayoutEffect(() => {
    if (!selectedCommentId) return;
    document
      .querySelector(
        `[data-testid="ask-v2-review-comment-card-${selectedCommentId}"]`,
      )
      ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedCommentId]);

  // Findings she has already decided on are grouped apart from the new ones,
  // so the panel reads as "what is new" vs "what is already done". Sections
  // appear only when some finding carries a past decision; a review with none
  // renders the flat list exactly as before.
  const sections = useMemo(() => {
    const newItems: CommentWithOptionalTags[] = [];
    const confirmedItems: CommentWithOptionalTags[] = [];
    const rejectedItems: CommentWithOptionalTags[] = [];
    for (const comment of comments) {
      if (comment.previouslyRejected) rejectedItems.push(comment);
      else if (comment.previouslyConfirmed) confirmedItems.push(comment);
      else newItems.push(comment);
    }
    return [
      { key: "new", labelKey: "askV2.review.sectionNew", items: newItems },
      {
        key: "confirmed",
        labelKey: "askV2.review.sectionConfirmed",
        items: confirmedItems,
      },
      {
        key: "rejected",
        labelKey: "askV2.review.sectionRejected",
        items: rejectedItems,
      },
    ];
  }, [comments]);
  // All three always render, empty ones included, so "Approved 0" says there
  // is nothing approved yet rather than leaving her to wonder where it went.
  const showSections = comments.length > 0;
  // Approved and Rejected start closed: they are decisions already made, and
  // the panel should open on what is new.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => new Set(["confirmed", "rejected"]),
  );
  // why: selecting a finding from the artwork (its pin) must never land on a
  // card hidden inside a closed section -- open the section that holds it.
  useEffect(() => {
    if (!selectedCommentId) return;
    const holder = sections.find((section) =>
      section.items.some((comment) => comment._id === selectedCommentId),
    );
    if (!holder) return;
    setCollapsedSections((prev) => {
      if (!prev.has(holder.key)) return prev;
      const next = new Set(prev);
      next.delete(holder.key);
      return next;
    });
  }, [selectedCommentId, sections]);
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderComment = (comment: CommentWithOptionalTags) => {
    const id = comment._id;
    const expanded = selectedCommentId === id;
    const title = reviewCommentTitle(comment);
    const commentNumber = commentNumbers[id] ?? 0;
    const collapsedSurface =
      "bg-puntt-neutral-gray-3 hover:bg-base-white transition-colors";
    const expandedSurface = "bg-base-white";
    // why: a demoted finding is one she already threw out. Dimmed and
    // dashed while collapsed so the list separates it at a glance (it is
    // also sorted last); full strength on hover or when opened, because
    // demote-not-drop means it must stay readable, never hidden.
    const demoted = Boolean(comment.previouslyRejected);
    const demotedSurface =
      demoted && !expanded ? " border-dashed opacity-60 hover:opacity-100" : "";

    return (
      <li key={id} className="mb-2">
        <div
          className={`overflow-hidden rounded-lg border border-puntt-neutral-gray-5 transition-opacity ${
            expanded ? expandedSurface : collapsedSurface
          }${demotedSurface}`}
          data-testid={`ask-v2-review-comment-card-${id}`}
          data-demoted={demoted ? "true" : undefined}
        >
          <button
            type="button"
            className={`w-full p-3 text-left ${
              expanded ? expandedSurface : collapsedSurface
            }`}
            data-testid={`ask-v2-review-comment-${id}`}
            aria-expanded={expanded}
            onClick={() => (expanded ? onClear() : onSelect(id))}
          >
            {expanded ? (
              <>
                <CommentIdentityRow
                  id={id}
                  commentNumber={commentNumber}
                  authorLabel={reviewCommentAuthorLabel(comment, authorOptions)}
                  createdAt={comment.createdAt}
                />
                <div className="mt-2 min-w-0 text-left">
                  <Text
                    as="p"
                    size="3"
                    weight="medium"
                    className="leading-snug text-puntt-neutral-gray-12"
                    data-testid={`ask-v2-review-comment-title-${id}`}
                  >
                    {title}
                  </Text>
                  <PreviouslyRejectedSubheading
                    comment={comment}
                    commentId={id}
                  />
                  <PreviouslyConfirmedSubheading
                    comment={comment}
                    commentId={id}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {comment.isRequired ? (
                      <Badge
                        size="1"
                        variant="solid"
                        color="red"
                        data-testid="ask-v2-review-comment-urgent-badge"
                      >
                        {t("askV2.review.urgentBadge")}
                      </Badge>
                    ) : null}
                    <CommentSeverityBadge severity={comment.severity} />
                    <CommentTagBadges comment={comment} commentId={id} />
                  </div>
                </div>
              </>
            ) : (
              <div className="line-clamp-5 text-left">
                <CommentIdentityRow
                  id={id}
                  commentNumber={commentNumber}
                  authorLabel={reviewCommentAuthorLabel(comment, authorOptions)}
                  createdAt={comment.createdAt}
                />
                <div className="mt-2 min-w-0">
                  <Text
                    as="p"
                    size="3"
                    weight="medium"
                    className="leading-snug text-puntt-neutral-gray-12"
                    data-testid={`ask-v2-review-comment-title-${id}`}
                  >
                    {title}
                  </Text>
                  <PreviouslyRejectedSubheading
                    comment={comment}
                    commentId={id}
                  />
                  <PreviouslyConfirmedSubheading
                    comment={comment}
                    commentId={id}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <CommentTagBadges comment={comment} commentId={id} />
                </div>
                <CollapsedCommentPreview comment={comment} />
              </div>
            )}
          </button>
          {expanded ? (
            <div
              className="bg-base-white px-3 pb-3 pt-0"
              data-testid={`ask-v2-review-comment-expanded-${id}`}
            >
              <JudgmentReceiptLink comment={comment} commentId={id} />
              <AskPunttReviewCommentBody
                commentId={id}
                description={comment.description}
                claimCitations={claimCitationsByComment.get(id)}
                afterExpand={
                  <AskPunttReviewCommentSource
                    comment={comment}
                    contextFiles={contextFiles}
                    reviewFiles={reviewFiles}
                  />
                }
              />
              {comment.isAI &&
              !comment.isPending &&
              !comment.isDeleted &&
              onJudge ? (
                <AskV2ReviewCommentJudgmentActions
                  commentId={id}
                  liked={comment.liked}
                  includeRejected={includeRejectedComments}
                  disabled={judgmentPending}
                  findingText={comment.description}
                  brand={citedRuleBrand(comment.citations)}
                  scopeChoices={citedRuleScopeChoices(comment.citations)}
                  onJudge={(liked, scope) => onJudge(id, liked, scope)}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comments"
      aria-label={t("askV2.review.findingsLabel")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <h2
          className="text-[13px] font-bold text-puntt-neutral-gray-12"
          data-testid="ask-v2-review-findings-title"
        >
          {t("askV2.review.findingsTitle")}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {rejectedCommentCount > 0 && onToggleIncludeRejected ? (
            <button
              type="button"
              className="text-[12px] font-medium text-puntt-neutral-gray-11 underline-offset-2 hover:underline"
              data-testid="ask-v2-review-show-rejected-toggle"
              aria-pressed={includeRejectedComments}
              onClick={onToggleIncludeRejected}
            >
              {t("askV2.review.showRejectedToggle", {
                count: rejectedCommentCount,
              })}
            </button>
          ) : null}
          {showTagFilter ? (
            <AskV2ReviewTagFilter
              activeTags={activeTags}
              onToggle={onToggleTag}
            />
          ) : null}
        </div>
      </div>
      {blocked.length > 0 ? (
        // Above the findings, because it qualifies them: a review that could
        // not read the artwork is short for a reason, and a person who reads
        // only the list below sees a review that found nothing.
        <div
          className="border-y border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 px-4 py-3"
          data-testid="ask-v2-review-could-not-check"
        >
          <h3 className="text-[12px] font-bold text-puntt-neutral-gray-12">
            {t("askV2.review.couldNotCheckTitle")}
          </h3>
          <ul className="mt-1.5 flex flex-col gap-1">
            {blocked.map((entry, index) => (
              <li
                // Not `check` alone: one check name can appear under two
                // artworks, and a review of several pairs would collide.
                key={`${entry.artwork}:${entry.check}:${index}`}
                className="text-[12px] leading-snug text-puntt-neutral-gray-10"
                data-testid={`ask-v2-review-could-not-check-${index}`}
              >
                {blockedSpansArtworks ? (
                  <span
                    className="text-puntt-neutral-gray-9"
                    data-testid={`ask-v2-review-could-not-check-artwork-${index}`}
                  >
                    {entry.artwork}
                    {" · "}
                  </span>
                ) : null}
                <span className="font-medium text-puntt-neutral-gray-11">
                  {entry.check}
                </span>
                {entry.reason ? ` — ${entry.reason}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ul
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-comment-list"
        aria-label={t("askV2.review.commentsLabel")}
      >
        {comments.length === 0 ? (
          <li
            className="px-2 py-6 text-center text-[13px] text-puntt-neutral-gray-10"
            data-testid={emptyStateTestId}
          >
            {emptyStateCopy}
          </li>
        ) : null}
        {showSections
          ? sections.map((section) => {
              const open = !collapsedSections.has(section.key);
              return (
                <li
                  key={section.key}
                  className="mb-3"
                  data-testid={`ask-v2-review-section-${section.key}`}
                >
                  <button
                    type="button"
                    className="mb-2 flex w-full items-center gap-2 rounded-md px-1 py-2 text-left text-[15px] font-semibold text-puntt-neutral-gray-12 hover:bg-puntt-neutral-gray-3"
                    data-testid={`ask-v2-review-section-${section.key}-toggle`}
                    aria-expanded={open}
                    onClick={() => toggleSection(section.key)}
                  >
                    <CaretUp
                      size={14}
                      weight="bold"
                      aria-hidden="true"
                      className={open ? "" : "rotate-180"}
                    />
                    <span>{t(section.labelKey)}</span>
                    <span
                      className="font-medium text-puntt-neutral-gray-10"
                      data-testid={`ask-v2-review-section-${section.key}-count`}
                    >
                      {section.items.length}
                    </span>
                  </button>
                  {open ? (
                    <ul
                      data-testid={`ask-v2-review-section-${section.key}-list`}
                    >
                      {section.items.map(renderComment)}
                    </ul>
                  ) : null}
                </li>
              );
            })
          : comments.map(renderComment)}
      </ul>
    </section>
  );
}

/**
 * The receipt a demoted finding carries, as a subheading under its title.
 *
 * why: a small gray chip among the tag badges read as metadata; the first
 * live check had to open the card to find it at all. Directly under the title,
 * with the reject icon, it is the second thing she reads about the finding --
 * on the collapsed card and the open one alike, which are mutually exclusive
 * per comment, so the testid stays unique on the page.
 */
function PreviouslyRejectedSubheading({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = comment.previouslyRejected;
  if (!receipt) return null;
  return (
    <Text
      as="p"
      size="2"
      weight="medium"
      className="mt-1 flex items-center gap-1.5 text-puntt-neutral-gray-11"
      data-testid={`ask-v2-review-comment-previously-rejected-${commentId}`}
    >
      <XCircle size={16} weight="bold" aria-hidden="true" />
      {receipt.role
        ? t("assetViewer.previouslyRejectedBy", { role: receipt.role })
        : t("assetViewer.previouslyRejected")}
    </Text>
  );
}

/**
 * A finding a reviewer already confirmed as an issue: green, under the title,
 * so it reads as agreed work rather than something new.
 */
function PreviouslyConfirmedSubheading({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = comment.previouslyConfirmed;
  if (!receipt || comment.previouslyRejected) return null;
  return (
    <Text
      as="p"
      size="2"
      weight="medium"
      color="green"
      // why: Radix's own green, as the Confirm button uses -- the tailwind
      // `text-puntt-green-11` class rendered gray in the live panel.
      className="mt-1 flex items-center gap-1.5"
      data-testid={`ask-v2-review-comment-previously-confirmed-${commentId}`}
    >
      <CheckCircle size={16} weight="bold" aria-hidden="true" />
      {receipt.role
        ? t("assetViewer.previouslyConfirmedBy", { role: receipt.role })
        : t("assetViewer.previouslyConfirmed")}
    </Text>
  );
}

/**
 * Where a past decision is logged: the knowledge rule the finding cited when
 * there is one, else the review it was made on. Rendered in the open card's
 * body, not the header, because the header is a button and a link inside a
 * button is invalid markup that swallows keyboard focus.
 */
function JudgmentReceiptLink({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = comment.previouslyRejected ?? comment.previouslyConfirmed;
  if (!receipt) return null;
  const className =
    "mb-2 inline-block text-[13px] font-medium text-puntt-blue-11 underline";
  if (receipt.ruleId) {
    return (
      <Link
        to={KNOWLEDGE_DATABASE_PATH}
        search={{
          ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
          tab: "rules",
          ruleId: receipt.ruleId,
        }}
        target="_blank"
        className={className}
        data-testid={`ask-v2-review-comment-judgment-rule-link-${commentId}`}
      >
        {t("assetViewer.viewJudgmentRule")}
      </Link>
    );
  }
  if (receipt.chatId && receipt.reviewId) {
    return (
      <Link
        // why: the typed route register names this path by its v1 param, `$ticketId`;
        // it is the same URL the v2 review route serves, so the link resolves there.
        to="/ask-puntt/$chatId/reviews/$ticketId"
        params={{ chatId: receipt.chatId, ticketId: receipt.reviewId }}
        target="_blank"
        className={className}
        data-testid={`ask-v2-review-comment-judgment-review-link-${commentId}`}
      >
        {t("assetViewer.viewJudgmentReview")}
      </Link>
    );
  }
  return null;
}
